var _class, _temp;

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash.isequal';
import DataContext from '../../context/Data';
import ScalerContext from '../../context/Scaler';
import GriffPropTypes, { seriesPropType } from '../../utils/proptypes';
import Axes from '../../utils/Axes';

// If the timeSubDomain is within this margin, consider it to be attached to
// the leading edge of the timeDomain.
var FRONT_OF_WINDOW_THRESHOLD = 0.05;

/**
 * Provide a placeholder domain so that we can test for validity later, but
 * it can be safely operated on like a real domain.
 */
export var PLACEHOLDER_DOMAIN = [0, 0];

var haveDomainsChanged = function haveDomainsChanged(before, after) {
  return before.timeDomain !== after.timeDomain || before.timeSubDomain !== after.timeSubDomain || before.xDomain !== after.xDomain || before.xSubDomain !== after.xSubDomain || before.yDomain !== after.yDomain || before.ySubDomain !== after.ySubDomain;
};

var findItemsWithChangedDomains = function findItemsWithChangedDomains(previousItems, currentItems) {
  var previousItemsById = previousItems.reduce(function (acc, s) {
    var _extends2;

    return _extends({}, acc, (_extends2 = {}, _extends2[s.id] = s, _extends2));
  }, {});
  return currentItems.reduce(function (acc, s) {
    if (!previousItemsById[s.id] || haveDomainsChanged(previousItemsById[s.id] || {}, s)) {
      return [].concat(acc, [s]);
    }
    return acc;
  }, []);
};

export var stripPlaceholderDomain = function stripPlaceholderDomain(domain) {
  if (isEqual(PLACEHOLDER_DOMAIN, domain)) {
    return undefined;
  }
  return domain;
};

/**
 * The scaler is the source of truth for all things related to the domains and
 * subdomains for all of the items within Griff. Note that an item can be either
 * a series or a collection, and domains are flexible. As of this writing, there
 * are three axes:
 *   time: The timestamp of a datapoint
 *   x: The x-value of a datapoint
 *   y: The y-value of a datapoint.
 *
 * These axes all have separate domains and subdomains. The domain is the range
 * of that axis, and the subdomain is the currently-visible region of that
 * range.
 *
 * These are manipulated with the {@link #updateDomains} function, which is
 * made available through the {@link ScalerContext}.
 */
var Scaler = (_temp = _class = function (_Component) {
  _inherits(Scaler, _Component);

  function Scaler(props) {
    _classCallCheck(this, Scaler);

    var _this = _possibleConstructorReturn(this, _Component.call(this, props));

    _this.getDomainsByItemId = function () {
      return [].concat(_this.props.dataContext.series).concat(_this.props.dataContext.collections).reduce(function (acc, item) {
        var _item$id, _extends3;

        return _extends({}, acc, (_extends3 = {}, _extends3[item.id] = (_item$id = {}, _item$id[Axes.time] = [].concat(_this.props.dataContext.timeDomain), _item$id[Axes.x] = [].concat(item.xDomain || PLACEHOLDER_DOMAIN), _item$id[Axes.y] = [].concat(item.yDomain || PLACEHOLDER_DOMAIN), _item$id), _extends3));
      }, {});
    };

    _this.getSubDomainsByItemId = function () {
      return [].concat(_this.props.dataContext.series).concat(_this.props.dataContext.collections).reduce(function (acc, item) {
        var _item$id2, _extends4;

        return _extends({}, acc, (_extends4 = {}, _extends4[item.id] = (_item$id2 = {}, _item$id2[Axes.time] = [].concat(_this.props.dataContext.timeSubDomain), _item$id2[Axes.x] = [].concat(item.xSubDomain || PLACEHOLDER_DOMAIN), _item$id2[Axes.y] = [].concat(item.ySubDomain || PLACEHOLDER_DOMAIN), _item$id2), _extends4));
      }, {});
    };

    _this.updateDomains = function (changedDomainsById, callback) {
      // FIXME: This is not multi-series aware.
      var newTimeSubDomain = null;

      var _this$state = _this.state,
          domainsByItemId = _this$state.domainsByItemId,
          subDomainsByItemId = _this$state.subDomainsByItemId;

      var newSubDomains = _extends({}, subDomainsByItemId);
      Object.keys(changedDomainsById).forEach(function (itemId) {
        newSubDomains[itemId] = _extends({}, subDomainsByItemId[itemId] || {});
        Object.keys(changedDomainsById[itemId]).forEach(function (axis) {
          var newSubDomain = changedDomainsById[itemId][axis];
          if (axis === String(Axes.time)) {
            newSubDomain = _this.props.dataContext.limitTimeSubDomain(newSubDomain);
          }

          var newSpan = newSubDomain[1] - newSubDomain[0];

          var existingSubDomain = subDomainsByItemId[itemId][axis] || newSubDomain;
          var existingSpan = existingSubDomain[1] - existingSubDomain[0];

          var limits = stripPlaceholderDomain(((domainsByItemId || {})[itemId] || {})[axis] || (axis === String(Axes.time) ? // FIXME: Phase out this single timeDomain thing.
          _this.props.dataContext.timeDomain : undefined)) || [Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER];

          if (newSpan === existingSpan) {
            // This is a translation; check the bounds.
            if (newSubDomain[0] <= limits[0]) {
              newSubDomain = [limits[0], limits[0] + newSpan];
            }
            if (newSubDomain[1] >= limits[1]) {
              newSubDomain = [limits[1] - newSpan, limits[1]];
            }
          } else {
            newSubDomain = [Math.max(limits[0], newSubDomain[0]), Math.min(limits[1], newSubDomain[1])];
          }

          newSubDomains[itemId][axis] = newSubDomain;

          if (axis === String(Axes.time)) {
            newTimeSubDomain = newSubDomain;
          }
        });
      });
      // expose newSubDomains to DataProvider
      if (_this.props.dataContext.onUpdateDomains) {
        _this.props.dataContext.onUpdateDomains(newSubDomains);
      }
      _this.setState({ subDomainsByItemId: newSubDomains }, callback ? function () {
        return callback(changedDomainsById);
      } : undefined);
      if (newTimeSubDomain) {
        _this.props.dataContext.timeSubDomainChanged(newTimeSubDomain);
      }
    };

    _this.state = {
      // Map from item (collection, series) to their respective domains.
      domainsByItemId: _this.getDomainsByItemId(),

      // Map from item (collection, series) to their respective subdomains.
      subDomainsByItemId: _this.getSubDomainsByItemId()
    };
    return _this;
  }

  Scaler.prototype.componentDidUpdate = function componentDidUpdate(prevProps) {
    var _this2 = this;

    var changedSeries = findItemsWithChangedDomains(prevProps.dataContext.series, this.props.dataContext.series);
    var changedCollections = findItemsWithChangedDomains(prevProps.dataContext.collections, this.props.dataContext.collections);
    if (changedSeries.length > 0 || changedCollections.length > 0) {
      var domainsByItemId = _extends({}, this.state.domainsByItemId);
      var subDomainsByItemId = _extends({}, this.state.subDomainsByItemId);

      [].concat(changedSeries).concat(changedCollections).forEach(function (item) {
        var _domainsByItemId$item, _subDomainsByItemId$i;

        domainsByItemId[item.id] = (_domainsByItemId$item = {}, _domainsByItemId$item[Axes.time] = _this2.props.dataContext.timeDomain || [].concat(item.timeDomain || stripPlaceholderDomain(Axes.time(_this2.state.domainsByItemId[item.id]))), _domainsByItemId$item[Axes.x] = [].concat(item.xDomain || stripPlaceholderDomain(Axes.x(_this2.state.domainsByItemId[item.id])) || PLACEHOLDER_DOMAIN), _domainsByItemId$item[Axes.y] = [].concat(item.yDomain || stripPlaceholderDomain(Axes.y(_this2.state.domainsByItemId[item.id])) || PLACEHOLDER_DOMAIN), _domainsByItemId$item);
        subDomainsByItemId[item.id] = (_subDomainsByItemId$i = {}, _subDomainsByItemId$i[Axes.time] = _this2.props.dataContext.timeSubDomain || [].concat(item.timeSubDomain || stripPlaceholderDomain(Axes.time(_this2.state.subDomainsByItemId[item.id]))), _subDomainsByItemId$i[Axes.x] = [].concat(item.xSubDomain || stripPlaceholderDomain(Axes.x(_this2.state.subDomainsByItemId[item.id])) || PLACEHOLDER_DOMAIN), _subDomainsByItemId$i[Axes.y] = [].concat(item.ySubDomain || stripPlaceholderDomain(Axes.y(_this2.state.subDomainsByItemId[item.id])) || PLACEHOLDER_DOMAIN), _subDomainsByItemId$i);
      });
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ subDomainsByItemId: subDomainsByItemId, domainsByItemId: domainsByItemId });
      return;
    }

    if (!isEqual(prevProps.dataContext.timeDomain, this.props.dataContext.timeDomain)) {
      var prevTimeDomain = prevProps.dataContext.timeDomain;
      var nextTimeDomain = this.props.dataContext.timeDomain;

      // When timeDomain changes, we need to update everything downstream.

      var _domainsByItemId = _extends({}, this.state.domainsByItemId);
      Object.keys(_domainsByItemId).forEach(function (itemId) {
        _domainsByItemId[itemId][Axes.time] = nextTimeDomain;
      });

      var _subDomainsByItemId = _extends({}, this.state.subDomainsByItemId);
      Object.keys(_subDomainsByItemId).forEach(function (itemId) {
        var timeSubDomain = _this2.state.subDomainsByItemId[itemId][Axes.time];

        _subDomainsByItemId[itemId] = _extends({}, _this2.state.subDomainsByItemId[itemId]);
        var dt = timeSubDomain[1] - timeSubDomain[0];
        if (Math.abs((timeSubDomain[1] - prevTimeDomain[1]) / dt) <= FRONT_OF_WINDOW_THRESHOLD) {
          // Looking at the front of the window -- continue to track that.
          _subDomainsByItemId[itemId][Axes.time] = [nextTimeDomain[1] - dt, nextTimeDomain[1]];
        } else if (timeSubDomain[0] <= prevTimeDomain[0]) {
          // Looking at the back of the window -- continue to track that.
          _subDomainsByItemId[itemId][Axes.time] = [prevTimeDomain[0], prevTimeDomain[0] + dt];
        }
      });

      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ domainsByItemId: _domainsByItemId, subDomainsByItemId: _subDomainsByItemId });

      if (!isEqual(prevProps.dataContext.timeSubDomain, this.props.dataContext.timeSubDomain)) {
        // When timeSubDomain changes, we need to update everything downstream.
        var newSubDomainsByItemId = {};
        Object.keys(this.state.subDomainsByItemId).forEach(function (itemId) {
          var _extends5;

          newSubDomainsByItemId[itemId] = _extends({}, _this2.state.subDomainsByItemId[itemId], (_extends5 = {}, _extends5[Axes.time] = _this2.props.dataContext.timeSubDomain, _extends5));
        });
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ subDomainsByItemId: newSubDomainsByItemId });
      }
    }
  };

  /**
   * Update the subdomains for the given items. This is a patch update and will
   * be merged with the current state of the subdomains. An example payload
   * will resemble:
   * <code>
   *   {
   *     "series-1": {
   *       "y": [0.5, 0.75],
   *     },
   *     "series-2": {
   *       "y": [1.0, 2.0],
   *     }
   *   }
   * </code>
   *
   * After this is complete, {@code callback} will be called with this patch
   * object.
   */


  Scaler.prototype.render = function render() {
    var _state = this.state,
        domainsByItemId = _state.domainsByItemId,
        subDomainsByItemId = _state.subDomainsByItemId;
    var dataContext = this.props.dataContext;

    var ownContext = {
      updateDomains: this.updateDomains,
      domainsByItemId: domainsByItemId,
      subDomainsByItemId: subDomainsByItemId
    };

    var enrichedContext = {
      timeSubDomain: dataContext.timeSubDomain || dataContext.timeDomain
    };

    var finalContext = _extends({}, dataContext, enrichedContext, ownContext);

    return React.createElement(
      ScalerContext.Provider,
      { value: finalContext },
      this.props.children
    );
  };

  return Scaler;
}(Component), _class.defaultProps = {}, _temp);
Scaler.propTypes = process.env.NODE_ENV !== "production" ? {
  children: PropTypes.node.isRequired,
  dataContext: PropTypes.shape({
    timeDomain: PropTypes.arrayOf(PropTypes.number).isRequired,
    timeSubDomain: PropTypes.arrayOf(PropTypes.number).isRequired,
    timeSubDomainChanged: PropTypes.func.isRequired,
    limitTimeSubDomain: PropTypes.func,
    externalXSubDomain: PropTypes.arrayOf(PropTypes.number),
    series: seriesPropType.isRequired,
    collections: GriffPropTypes.collections.isRequired,
    onUpdateDomains: PropTypes.func
  }).isRequired
} : {};


export default (function (props) {
  return React.createElement(
    DataContext.Consumer,
    null,
    function (dataContext) {
      return React.createElement(Scaler, _extends({}, props, { dataContext: dataContext }));
    }
  );
});